import React, { useCallback, useState } from 'react'

import { useDragToPan } from '@/hooks/useDragToPan'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useImageZoom } from '@/hooks/useImageZoom'
import { useLightMode } from '@/hooks/useLightMode'

import {
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { Box as DarkBox, Modal as DarkModal } from '@hivewatch/dark'

import { MediaPreviewerControls } from '@/components/media-previewer-controls/MediaPreviewerControls'
import { Modal } from '@/components/overlays'
import { ZoomableImage } from '@/components/zoomable-image/ZoomableImage'

import { IncidentNoteVideoPlayer } from '../../add-note/components/IncidentNoteVideoPlayer'
import { IncidentNotesMediaType } from '../../shared/types/feedTypes'

interface MediaPreviewerModalIProps {
  onClose: () => void
  isOpen: boolean
  file: IncidentNotesMediaType
}

const getModalBodyStyles = (isFull: boolean) => ({
  padding: isFull ? '10px' : '20px',
  maxWidth: isFull ? '100%' : '900px',
  maxHeight: isFull ? '100%' : '900px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const MediaPreviewerModal = ({
  file,
  onClose,
  isOpen,
}: MediaPreviewerModalIProps) => {
  const { mimeType, url, name } = file
  const [modalSize, setModalSize] = useState('3xl' as '3xl' | 'full')
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()

  const mediaType = {
    isImage: mimeType.includes('image'),
    isVideo: mimeType.includes('video'),
    isPdf: mimeType.includes('pdf'),
  }

  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    isZoomed,
  } = useImageZoom(isOpen)

  const { containerRef, isDragging, handlers } = useDragToPan(isZoomed)

  const handleToggleModalSize = useCallback(() => {
    const newSize = modalSize === '3xl' ? 'full' : '3xl'
    setModalSize(newSize)
    if (newSize === '3xl') {
      resetZoom()
    }
  }, [modalSize, resetZoom])

  const renderMediaContent = () => {
    if (mediaType.isVideo) {
      return <IncidentNoteVideoPlayer videoUrl={url} />
    }

    if (mediaType.isImage) {
      return (
        <ZoomableImage
          containerRef={containerRef}
          isDragging={isDragging}
          modalSize={modalSize}
          name={name}
          onToggleModalSize={handleToggleModalSize}
          url={url}
          zoomLevel={zoomLevel}
          {...handlers}
        />
      )
    }

    if (mediaType.isPdf) {
      return (
        <embed height={800} src={url} type='application/pdf' width='100%' />
      )
    }

    return null
  }

  if (isRebrand) {
    const isFull = modalSize === 'full'
    return (
      <DarkModal
        bodyStyleOverrides={getModalBodyStyles(isFull)}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW={isFull ? '100vw' : '900px'}
        onClose={onClose}
        title={name}
        zIndex={1500}
      >
        <MediaPreviewerControls
          modalSize={modalSize}
          onToggleModalSize={handleToggleModalSize}
          zoomActions={{
            onReset: resetZoom,
            onZoomIn: zoomIn,
            onZoomOut: zoomOut,
          }}
          zoomState={{
            canZoomIn,
            canZoomOut,
            isZoomed,
            shouldShowControls: mediaType.isImage && isFull,
          }}
        />
        <DarkBox
          alignItems='center'
          display='flex'
          justifyContent='center'
          maxH={isFull ? '100%' : '900px'}
          maxW={isFull ? '100%' : '900px'}
          p={isFull ? '10px' : '20px'}
        >
          {renderMediaContent()}
        </DarkBox>
      </DarkModal>
    )
  }

  return (
    <Modal
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={onClose}
      size={modalSize}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize='16px'>{name}</ModalHeader>
        <ModalCloseButton />

        <MediaPreviewerControls
          modalSize={modalSize}
          onToggleModalSize={handleToggleModalSize}
          zoomActions={{
            onReset: resetZoom,
            onZoomIn: zoomIn,
            onZoomOut: zoomOut,
          }}
          zoomState={{
            canZoomIn,
            canZoomOut,
            isZoomed,
            shouldShowControls: mediaType.isImage && modalSize === 'full',
          }}
        />

        <ModalBody sx={getModalBodyStyles(modalSize === 'full')}>
          {renderMediaContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
